import { MultiSelect } from "react-multi-select-component";
import { Gender, Issues, Languages } from "../../Config/Expert";

const PROFESSION_OPTIONS = [
    { value: "clinical_psychologist", label: "Clinical Psychologist" },
    { value: "psychiatrist", label: "Psychiatrist" },
    { value: "counseling_psychologist", label: "Counseling Psychologist" },
    { value: "creative_art_therapist", label: "Creative Art Therapist" },
    { value: "yoga_or_meditation_expert", label: "Yoga or Meditation Expert" },
    {
        value: "associate_clinical_psychologist",
        label: "Associate Clinical Psychologist",
    },
    { value: "neuropsychologist", label: "Neuropsychologist" },
    { value: "nutritionist", label: "Nutritionist" },
];

const wordCount = (text: string) => {
    const trimmed = (text || "").trim();
    if (!trimmed) {
        return 0;
    }
    return trimmed.split(/\s+/).length;
};

const selectedOptions = (values: string[] = [], options: any[]) =>
    options.filter((option) => (values || []).includes(option.value));

type ExpertProfileOnboardingFieldsProps = {
    expertDetails: any;
    setExpertDetails: (next: any) => void;
    certificationInput: string;
    setCertificationInput: (value: string) => void;
};

export const ExpertProfileOnboardingFields = ({
    expertDetails,
    setExpertDetails,
    certificationInput,
    setCertificationInput,
}: ExpertProfileOnboardingFieldsProps) => {
    const introductionWords = wordCount(expertDetails.profile_introduction);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (name === "profile_introduction") {
            if (wordCount(value) > 50) {
                return;
            }
        }
        setExpertDetails({
            ...expertDetails,
            [name]: value,
        });
    };

    return (
        <>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="date_of_birth">
                    Date of Birth
                </label>
                <input
                    onChange={handleChange}
                    type="date"
                    className="form-control"
                    id="date_of_birth"
                    name="date_of_birth"
                    value={expertDetails.date_of_birth || ""}
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="holds_professional_license">
                    Do you hold any professional license or registration?
                </label>
                <select
                    className="form-control"
                    id="holds_professional_license"
                    name="holds_professional_license"
                    value={String(Boolean(expertDetails.holds_professional_license))}
                    onChange={(e) =>
                        setExpertDetails({
                            ...expertDetails,
                            holds_professional_license: e.target.value === "true",
                            professional_license_details:
                                e.target.value === "true"
                                    ? expertDetails.professional_license_details
                                    : "",
                        })
                    }
                >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                </select>
            </div>
            {expertDetails.holds_professional_license && (
                <div className="form-group col-md-12 mb-4">
                    <label className="form-label" htmlFor="professional_license_details">
                        License / registration details
                    </label>
                    <input
                        onChange={handleChange}
                        type="text"
                        className="form-control"
                        id="professional_license_details"
                        name="professional_license_details"
                        value={expertDetails.professional_license_details || ""}
                        placeholder="e.g. RCI number, registration body"
                    />
                </div>
            )}
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="outside_work">
                    Tell us a little about yourself beyond your professional work. You may
                    include your hobbies, interests, passions, or anything else that
                    reflects your personality.
                </label>
                <textarea
                    onChange={handleChange}
                    className="form-control"
                    id="outside_work"
                    name="outside_work"
                    rows={4}
                    value={expertDetails.outside_work || ""}
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="profession">
                    Profession
                </label>
                <select
                    className="form-control"
                    id="profession"
                    name="profession"
                    value={expertDetails.profession || ""}
                    onChange={handleChange}
                >
                    <option value="">Select Profession</option>
                    {PROFESSION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="top_expertise">
                    What are your top 5 areas of expertise? Please list the five areas you
                    would like to highlight on your profile. These will be displayed
                    prominently on the front page of the app.
                </label>
                <MultiSelect
                    options={Issues}
                    labelledBy="Select top 5 areas of expertise"
                    value={selectedOptions(expertDetails.top_expertise, Issues)}
                    onChange={(selected: any[]) => {
                        if (selected.length > 5) {
                            return;
                        }
                        setExpertDetails({
                            ...expertDetails,
                            top_expertise: selected.map((item) => item.value),
                        });
                    }}
                />
                <small className="text-muted">
                    {(expertDetails.top_expertise || []).length}/5 selected
                </small>
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="all_expertise">
                    Please select all areas of expertise that apply to you. You can choose
                    additional specializations beyond your top 5 to help us accurately
                    match you with a wider range of clients and therapy requirements.
                </label>
                <MultiSelect
                    options={Issues}
                    labelledBy="Select all areas of expertise"
                    value={selectedOptions(expertDetails.all_expertise, Issues)}
                    onChange={(selected: any[]) =>
                        setExpertDetails({
                            ...expertDetails,
                            all_expertise: selected.map((item) => item.value),
                        })
                    }
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="profile_gender">
                    Gender
                </label>
                <select
                    className="form-control"
                    id="profile_gender"
                    name="profile_gender"
                    value={expertDetails.profile_gender || ""}
                    onChange={handleChange}
                >
                    <option value="">Select Gender</option>
                    {Gender.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="highest_education">
                    Highest Educational Qualification
                </label>
                <input
                    onChange={handleChange}
                    type="text"
                    className="form-control"
                    id="highest_education"
                    name="highest_education"
                    value={expertDetails.highest_education || ""}
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="professional_certifications">
                    Professional Certifications
                </label>
                <div className="input-group">
                    <input
                        value={certificationInput}
                        onChange={(e) => setCertificationInput(e.target.value)}
                        type="text"
                        className="form-control"
                        id="professional_certifications"
                        name="professional_certifications"
                    />
                    <button
                        className="btn btn-primary ml-2"
                        type="button"
                        onClick={() => {
                            const nextValue = (certificationInput || "").trim();
                            if (!nextValue) {
                                return;
                            }
                            setExpertDetails({
                                ...expertDetails,
                                professional_certifications: [
                                    ...(expertDetails.professional_certifications || []),
                                    nextValue,
                                ],
                            });
                            setCertificationInput("");
                        }}
                    >
                        Add
                    </button>
                </div>
            </div>
            {(expertDetails.professional_certifications || []).length > 0 && (
                <ul className="added-data-list">
                    {expertDetails.professional_certifications.map(
                        (item: string, index: number) => (
                            <li key={`${item}-${index}`}>
                                {item}
                                <span
                                    className="close-icon"
                                    onClick={() => {
                                        const professional_certifications = [
                                            ...(expertDetails.professional_certifications || []),
                                        ];
                                        professional_certifications.splice(index, 1);
                                        setExpertDetails({
                                            ...expertDetails,
                                            professional_certifications,
                                        });
                                    }}
                                >
                                    &#10006;
                                </span>
                            </li>
                        )
                    )}
                </ul>
            )}
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="practicing_experience_years">
                    Total Years Of Practicing Experience (do not add experience during
                    your degree/ or certificate)
                </label>
                <input
                    onChange={handleChange}
                    type="text"
                    className="form-control"
                    id="practicing_experience_years"
                    name="practicing_experience_years"
                    value={expertDetails.practicing_experience_years || ""}
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="languages_known">
                    Languages you know
                </label>
                <MultiSelect
                    options={Languages}
                    labelledBy="Select languages"
                    value={selectedOptions(expertDetails.languages_known, Languages)}
                    onChange={(selected: any[]) =>
                        setExpertDetails({
                            ...expertDetails,
                            languages_known: selected.map((item) => item.value),
                        })
                    }
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="profile_introduction">
                    Please write a short introduction (maximum 50 words) that will appear
                    at the top of your profile and be the first thing clients see on the
                    Manoshala app.
                </label>
                <textarea
                    onChange={handleChange}
                    className="form-control"
                    id="profile_introduction"
                    name="profile_introduction"
                    rows={3}
                    value={expertDetails.profile_introduction || ""}
                />
                <small className="text-muted">{introductionWords}/50 words</small>
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="why_therapist">
                    Why did you become a therapist?
                </label>
                <textarea
                    onChange={handleChange}
                    className="form-control"
                    id="why_therapist"
                    name="why_therapist"
                    rows={4}
                    value={expertDetails.why_therapist || ""}
                />
            </div>
            <div className="form-group col-md-12 mb-4">
                <label className="form-label" htmlFor="therapy_approach">
                    What is your approach in therapy?
                </label>
                <textarea
                    onChange={handleChange}
                    className="form-control"
                    id="therapy_approach"
                    name="therapy_approach"
                    rows={4}
                    value={expertDetails.therapy_approach || ""}
                />
            </div>
        </>
    );
};